'use client';

import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { faChevronLeft, faChevronRight, faStar, faX } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Slider from 'react-slick';

import { defaultDataTagGroup } from '@/constants/defaultSeo';

import { PropsWithClasses } from '@lamesarv-sdk/types';
import { IComponentReviewsSection } from '@/types/cms';

const MAX_REVIEW_LENGTH = 230;

interface IReviewSliderArrowProps {
  isRight?: boolean;
  onClick?: () => void;
}

const ReviewSliderArrow = ({ isRight, onClick }: IReviewSliderArrowProps) => (
  <button
    type="button"
    onClick={onClick}
    className={twMerge(
      'absolute top-1/2 -translate-y-1/2 flex items-center justify-center cursor-pointer z-10 outline-none size-8 text-black-500',
      isRight ? '-right-8' : '-left-8',
    )}
  >
    <FontAwesomeIcon className="size-6" icon={isRight ? faChevronRight : faChevronLeft} />
  </button>
);

interface IReviewProps extends PropsWithClasses {
  review: IComponentReviewsSection['reviews'][number];
}

const Review = ({ className, review }: IReviewProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const isTooLong = review.review.length > MAX_REVIEW_LENGTH;
  const showReadMore = isTooLong && !isOpen;

  return (
    <div
      className={twMerge(
        'rounded-xl bg-white px-8 py-6 flex flex-col items-start gap-3.5 md:p-6 lg:px-10 lg:py-8 lg:gap-3',
        review.classes,
        className,
      )}
    >
      <div className="flex justify-between w-full">
        <div className="flex gap-1 text-yellow-500">
          <FontAwesomeIcon icon={faStar} className="size-5" />
          <FontAwesomeIcon icon={faStar} className="size-5" />
          <FontAwesomeIcon icon={faStar} className="size-5" />
          <FontAwesomeIcon icon={faStar} className="size-5" />
          <FontAwesomeIcon icon={faStar} className="size-5" />
        </div>
        {isTooLong && isOpen && (
          <button type="button" onClick={() => setIsOpen(false)} className="cursor-pointer lg:hidden">
            <FontAwesomeIcon icon={faX} className="size-4 block" />
          </button>
        )}
      </div>
      <div className={twMerge('text-sm md:text-base lg:hidden', review.classes_review)}>
        {showReadMore ? review.review.slice(0, MAX_REVIEW_LENGTH) + '...' : review.review}
      </div>
      <p className={twMerge('max-lg:hidden text-base', review.classes_review)}>{review.review}</p>
      {showReadMore && (
        <button
          type="button"
          className="underline text-sm text-blue-500 font-medium hover:text-blue-600 cursor-pointer lg:hidden"
          onClick={() => setIsOpen(true)}
        >
          Read More
        </button>
      )}
      <div className={twMerge('text-[14px] md:text-[16px] font-bold', review.classes_author)}>{review.author}</div>
      <div className={twMerge('text-[14px]', review.classes_state)}>{review.state}</div>
    </div>
  );
};

export const ReviewsSection = ({
  classes,
  classes_description,
  classes_title,
  data_tag_group,
  data_tag,
  description,
  reviews,
  title,
}: IComponentReviewsSection) => {
  const reviewsDesktop = reviews.filter((item) => item.display_on_desktop).slice(0, 3);
  const reviewsTablet = reviews.filter((item) => item.display_on_tablet).slice(0, 4);
  const reviewsMobile = reviews.filter((item) => item.display_on_mobile).slice(0, 4);

  return (
    <div
      className={twMerge('bg-blue-50 py-16 md:py-24 font-helvetica text-black-500', classes)}
      data-tag-group={data_tag_group || defaultDataTagGroup}
      data-tag={data_tag ? `${data_tag}--reviews-section` : 'reviews-section'}
    >
      <div className="px-6 md:px-10 lg:container lg:mx-auto">
        {(title || description) && (
          <div className="flex flex-col gap-4 mb-8 md:mb-11 lg:mb-12">
            {title && (
              <h2 className={twMerge('text-blue-700 text-2xl font-bold md:text-[2.125rem]', classes_title)}>{title}</h2>
            )}
            {description && (
              <p className={twMerge('text-sm text-blue-700 md:text-base', classes_description)}>{description}</p>
            )}
          </div>
        )}
      </div>
      <div className="px-6 md:px-10 lg:container lg:mx-auto">
        {reviewsDesktop.length > 0 && (
          <div className="w-full hidden xl:flex flex-row justify-between items-stretch gap-6">
            {reviewsDesktop.map((review, index) => (
              <Review key={review.author + index} review={review} className="grow w-full" />
            ))}
          </div>
        )}
      </div>
      <div className="relative px-12">
        {reviewsTablet.length > 0 && (
          <div className="hidden md:block xl:hidden">
            {reviewsTablet.length > 2 ? (
              <Slider
                arrows={reviewsTablet.length > 1}
                nextArrow={<ReviewSliderArrow isRight />}
                prevArrow={<ReviewSliderArrow />}
                slidesToScroll={2}
                slidesToShow={2}
              >
                {reviewsTablet.map((review, index) => (
                  <Review key={index + review.author} review={review} className="mx-2 h-full" />
                ))}
              </Slider>
            ) : (
              <div className="w-full flex flex-row justify-between items-start gap-2">
                {reviewsTablet.map((review, index) => (
                  <Review key={index + review.author} review={review} className="grow w-full" />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="relative px-12">
        {reviewsMobile.length > 0 && (
          <div className="md:hidden">
            {reviewsMobile.length > 1 ? (
              <Slider
                arrows={reviewsMobile.length > 1}
                nextArrow={<ReviewSliderArrow isRight />}
                prevArrow={<ReviewSliderArrow />}
                slidesToScroll={1}
                slidesToShow={1}
              >
                {reviewsMobile.map((review, index) => (
                  <Review key={index + review.author} review={review} className="mx-2 h-full" />
                ))}
              </Slider>
            ) : (
              <Review review={reviewsMobile[0]} className="grow w-full" />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
